'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReportConfig } from '@effektify/types/definitions';
import type { ComponentSlots } from '@effektify/engine';
import type React from 'react';
import Engine from '@effektify/engine';
import { styled } from '@effektify/theme';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import Image from 'next/image';
import { useCallback, useMemo } from 'react';
import { isReportConfig } from '@effektify/types/guards';
import { KNOWN_FEATURES } from 'src/constants/features';
import useFeatures from 'src/hooks/useFeatures';
import { FlexColumn } from 'src/components/Atoms/FlexBox';
import Button from '@mui/material/Button';
import { GoalEditButton } from 'src/components/Molecules/GoalEditButton';
import { BookAMeetingBanner } from 'src/components/Organisms/BookAMeetingBanner';
import { Goals } from 'src/components/Organisms/Goals';
import useTranslations from 'src/hooks/useTranslations';
const textSpacer = '\u00A0';
const Spacer = styled(FlexColumn)(({
  theme
}) => ({
  height: theme.sizes.spacing['04']
}));

// Defined at module scope: declaring these inline gives them a new component
// type on every render, which makes React unmount and remount the whole report.
const ImageSlot = t0 => {
  const $ = _c(6);
  const {
    src,
    alt,
    width,
    height
  } = t0;
  const t1 = alt || "";
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      objectFit: "cover"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== height || $[2] !== src || $[3] !== t1 || $[4] !== width) {
    t3 = <Image src={src} alt={t1} width={width} height={height} style={t2} />;
    $[1] = height;
    $[2] = src;
    $[3] = t1;
    $[4] = width;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
const ButtonSlot = t0 => {
  const $ = _c(3);
  const {
    onClick,
    children
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== onClick) {
    t1 = <Button onClick={onClick} variant="outlined">{children}</Button>;
    $[0] = children;
    $[1] = onClick;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
interface ReportProps {
  report: ReportConfig | null;
  isLoading?: boolean;
}
const ReportWrapper = styled(FlexColumn)(() => ({
  width: '100%',
  height: '100%',
  position: 'relative'
}));
const Report: React.FC<ReportProps> = t0 => {
  const $ = _c(31);
  const {
    report,
    isLoading: t1
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const {
    isEnabled
  } = useFeatures();
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    t
  } = useTranslations();
  let t2;
  if ($[0] !== router) {
    t2 = url => {
      router.push(url);
    };
    $[0] = router;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleNavigate = t2;
  let t3;
  if ($[2] !== pathname || $[3] !== router || $[4] !== searchParams) {
    t3 = contentKey => {
      const newParams = new URLSearchParams(searchParams);
      if (contentKey) {
        newParams.set("helpKey", contentKey);
      } else {
        newParams.delete("helpKey");
      }
      router.replace(pathname + "?" + newParams.toString(), {
        scroll: false
      });
    };
    $[2] = pathname;
    $[3] = router;
    $[4] = searchParams;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleHelpClick = t3;
  let t4;
  if ($[6] !== isEnabled) {
    t4 = isEnabled(KNOWN_FEATURES.ALLOW_GOAL_EDITING);
    $[6] = isEnabled;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const goalEditingEnabled = t4;
  let t5;
  if ($[8] !== goalEditingEnabled) {
    t5 = {
      Image: ImageSlot,
      Button: ButtonSlot,
      ...(goalEditingEnabled ? {
        GoalEditButton
      } : {})
    };
    $[8] = goalEditingEnabled;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const customComponents = t5;
  let t6;
  if ($[10] !== t) {
    t6 = t("reportPage.help.cardHelpTooltip");
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== handleHelpClick || $[13] !== t6) {
    t7 = {
      label: t6,
      onClick: handleHelpClick
    };
    $[12] = handleHelpClick;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const helpContent = t7;
  if (!isReportConfig(report) || isLoading) {
    return null;
  }
  let t8;
  if ($[15] !== isEnabled || $[16] !== isLoading) {
    t8 = isEnabled(KNOWN_FEATURES.SET_GOALS_MODAL) && <Goals loading={isLoading} />;
    $[15] = isEnabled;
    $[16] = isLoading;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== customComponents || $[19] !== handleNavigate || $[20] !== helpContent || $[21] !== isLoading || $[22] !== report) {
    t9 = report && <Engine content={report} loading={isLoading} onNavigate={handleNavigate} components={customComponents} helpContent={helpContent} />;
    $[18] = customComponents;
    $[19] = handleNavigate;
    $[20] = helpContent;
    $[21] = isLoading;
    $[22] = report;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== isEnabled) {
    t10 = isEnabled(KNOWN_FEATURES.BOOK_A_MEETING_PROMO) && <BookAMeetingBanner />;
    $[24] = isEnabled;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Spacer>{textSpacer}</Spacer>;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10 || $[28] !== t8 || $[29] !== t9) {
    t12 = <ReportWrapper>{t8}{t9}{t10}{t11}</ReportWrapper>;
    $[27] = t10;
    $[28] = t8;
    $[29] = t9;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  return t12;
};
export default Report;